import { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography, Stack } from '@mui/material';
import { makeStyles } from '@mui/styles';
import heroVideo from '../../assets/herovideo2.mp4';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    minHeight: '100vh',
    width: '100%',
    overflow: 'hidden',
    backgroundColor: '#0a0a0a',
  },
  video: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 0,
  },
  fadeLeft: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background:
      'linear-gradient(to right, #0a0a0a 0%, rgba(8,8,8,0.7) 12%, rgba(8,8,8,0.18) 28%, transparent 46%)',
    zIndex: 1,
  },
  fadeRight: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background:
      'linear-gradient(to left, #0a0a0a 0%, rgba(8,8,8,0.5) 6%, rgba(8,8,8,0.15) 16%, transparent 32%)',
    zIndex: 1,
  },
  fadeY: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background:
      'linear-gradient(#0a0a0a 0%, transparent 10%, transparent 90%, #0a0a0a 100%)',
    zIndex: 1,
  },
  spot: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background:
      'radial-gradient(38% 60% at 76% 50%, rgba(0,0,0,0.55) 0%, transparent 100%)',
    zIndex: 1,
  },
  gridContainer: {
    position: 'relative',
    zIndex: 2,
    maxWidth: 1280,
    margin: '0 auto',
    width: '100%',
    paddingTop: 64,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    minHeight: 'calc(100vh - 64px)',
    alignItems: 'center',
    '@media (max-width:900px)': {
      gridTemplateColumns: '1fr',
    },
  },
  textCol: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: 36,
    justifyContent: 'center',
    maxWidth: 540,
    padding: '48px 0 48px 190px',
    '@media (max-width:1100px)': { padding: '48px 0 48px 32px' },
    '@media (max-width:900px)': { padding: '48px 24px', gap: 24 },
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '0 -40px 0 80px',
      background:
        'radial-gradient(60% 70% at 50% 50%, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.12) 55%, transparent 100%)',
      backdropFilter: 'blur(2px)',
      WebkitBackdropFilter: 'blur(2px)',
      zIndex: -1,
      pointerEvents: 'none',
    },
  },
  headline: {
    fontSize: 68,
    fontWeight: 700,
    lineHeight: 1.05,
    letterSpacing: '-2.5px',
    color: '#FFFFFF',
    margin: 0,
    '@media (max-width:1100px)': { fontSize: 56 },
    '@media (max-width:600px)': { fontSize: 40, letterSpacing: '-1.5px' },
  },
  subhead: {
    fontSize: 17,
    fontWeight: 400,
    lineHeight: 1.6,
    color: '#999',
    maxWidth: 420,
    margin: 0,
  },
  primaryCta: {
    backgroundColor: theme.palette.primary.main,
    color: '#0a0a0a',
    padding: '12px 24px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      filter: 'brightness(0.95)',
      boxShadow: 'none',
    },
  },
  secondaryCta: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(8px)',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    border: '1px solid rgba(255,255,255,0.08)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
}));

export default function Hero() {
  const classes = useStyles();
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = 0.5;
  }, []);

  return (
    <Box className={classes.root}>
      <video
        ref={videoRef}
        className={classes.video}
        src={heroVideo}
        autoPlay
        muted
        loop
        playsInline
      />
      <Box className={classes.fadeLeft} />
      <Box className={classes.fadeRight} />
      <Box className={classes.fadeY} />
      <Box className={classes.spot} />

      <Box className={classes.gridContainer}>
        <div />
        <Box className={classes.textCol}>
          <Box>
            <Typography component="h1" className={classes.headline}>
              ALL IN ONE
            </Typography>
            <Typography component="h1" className={classes.headline}>
              AI STUDIO.
            </Typography>
          </Box>
          <Typography className={classes.subhead}>
            Create voices, stories, product videos, and viral-ready content
            from one simple AI workspace.
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            sx={{ flexWrap: 'wrap' }}
          >
            <Button
              className={classes.primaryCta}
              disableElevation
              href="https://app.sorisori.ai/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Start Creating&nbsp;→
            </Button>
            <Button
              className={classes.secondaryCta}
              disableElevation
              component={RouterLink}
              to="/products"
            >
              View Product
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
